<template>
  <div name="molecule-select-customer" class="select-customer-card">
    <div class="card__header">
      <span class="card__header--topic">Create New Service Contract</span>
    </div>
    <div class="optional-contract-id mb-1">
      <atom-checkbox
        v-model="isUseOptionalContractId"
        idx="enable-optional-contract-id"
        :title="`Optional ${contractIdLabel}`"
        :isColorTextOnActive="true"
      ></atom-checkbox>
      <div class="optional-contract-id-input__container mt-1">
        <atom-input
          v-if="isUseOptionalContractId"
          v-model="optionalContractId"
          :title="contractIdLabel"
          errorMessage="Optional ID is support only 1-9999"
          placeholder="1-9999"
          type="number"
          :isAsterisk="true"
          :isError="isErrorAtOptionalContractId"
        >
        </atom-input>
      </div>
    </div>
    <section v-if="!isAddingNewCustomer" name="select-customer">
      <div class="select-customer-header">
        <div class="selector-container">
          <atom-select
            v-model="selectedCustomer"
            title="Select Customer"
            placeholder="Select Customer"
            label="customerName"
            :isAsterisk="true"
            :isClearable="false"
            :isAppendToBody="false"
            :isEnableProper="false"
            :isError="isErrorAtSelectCustomer"
            :errorMessage="errorMessageAtSelector"
            :option="customerOption"
          ></atom-select>
        </div>

        <div class="add-new-customer-button-container">
          <atom-button
            v-if="hasCreateCustomerPermission"
            buttonId="add-new-customer-button"
            buttonStyle="border"
            class="mt-1 mt-md-0 ml-0 ml-md-1"
            @click="onClickAddNewContract"
          >
            <span class="button-text">Add new customer</span>
          </atom-button>
        </div>
      </div>

      <div v-if="isShowCustomerAddresses" class="customer-address">
        <div class="address-card-container">
          <molecule-address-card
            class="mb-2"
            addressType="MAIN ADDRESS"
            :addressDetail="selectedCustomerMainAddress"
          ></molecule-address-card>
          <b-collapse v-model="isShowAllAddress" id="others-address">
            <molecule-address-card
              class="mb-2"
              addressType="BILLING ADDRESS"
              :addressDetail="selectedCustomerBillingAddress"
            ></molecule-address-card>
            <molecule-address-card
              addressType="MAILING ADDRESS"
              :addressDetail="selectedCustomerMailingAddress"
            ></molecule-address-card>
          </b-collapse>
          <div
            class="d-flex justify-content-center align-items-center custom-hover-effect"
            :class="{ 'pt-2': isShowAllAddress }"
            @click="toggleShowAddress"
          >
            <span v-if="!isShowAllAddress">Show More Addresses</span>
            <span v-else>See less</span>
            <feather-icon
              ref="chevron"
              icon="ChevronDownIcon"
              size="14"
              class="custom-margin-left-8"
            />
          </div>
        </div>
      </div>
    </section>

    <section v-else name="add-new-customer">
      <hr />
      <div class="add-new-customer-container">
        <div class="add-new-customer-header">
          <span class="add-new-customer-topic--text">Add new Customer</span>
          <div class="close-icon-container">
            <feather-icon
              class="icon-size-24px"
              icon="XIcon"
              stroke="#b9b9c3"
              @click="onClickCloseAddingNewContract"
            />
          </div>
        </div>
        <div class="add-new-customer-body">
          <div class="mb-2 input-container">
            <atom-input
              v-model="newCustomerData.name"
              title="Customer name"
              :isAsterisk="true"
              :isError="isErrorAtNewCustomerName"
              :errorMessage="errorMessageAtInput"
            >
            </atom-input>
          </div>
          <div name="addresses-card" class="row">
            <div name="main-address-card" class="col-12 col-lg-4">
              <molecule-create-edit-address-card
                v-model="newCustomerData.mainAddress"
                addressType="MAIN ADDRESS"
                :hasEditPermission="hasCreateCustomerPermission"
                :businessTypeOption="businessTypeOption"
                :isValidateAddressTrigger="isValidateNewAddressTrigger"
                @onUpdateErrorInCreateEditAddress="onUpdateErrorInMainAddress"
              ></molecule-create-edit-address-card>
            </div>
            <div
              name="billing-address-card"
              class="col-12 col-lg-4 mt-3 mt-lg-0"
            >
              <molecule-create-edit-address-card
                v-model="newCustomerData.billingAddress"
                addressType="BILLING ADDRESS"
                :hasEditPermission="hasCreateCustomerPermission"
                :businessTypeOption="businessTypeOption"
                :isValidateAddressTrigger="isValidateNewAddressTrigger"
                :main-address-detail="newCustomerData.mainAddress"
                @onVatValueChange="onVatValueChange"
                @onUpdateErrorInCreateEditAddress="
                  onUpdateErrorInBillingAddress
                "
              ></molecule-create-edit-address-card>
            </div>
            <div
              name="mailing-address-card"
              class="col-12 col-lg-4 mt-3 mt-lg-0"
            >
              <molecule-create-edit-address-card
                v-model="newCustomerData.mailingAddress"
                addressType="MAILING ADDRESS"
                :hasEditPermission="hasCreateCustomerPermission"
                :businessTypeOption="businessTypeOption"
                :isValidateAddressTrigger="isValidateNewAddressTrigger"
                :main-address-detail="newCustomerData.mainAddress"
                @onUpdateErrorInCreateEditAddress="
                  onUpdateErrorInMailingAddress
                "
              ></molecule-create-edit-address-card>
            </div>
          </div>
          <div
            class="d-flex justify-content-end mt-2 confirm-add-new-customer-button-container"
          >
            <atom-button
              buttonId="confirm-add-customer-button"
              :isDisable="isDisableAddNewCustomerButton"
              @click="onClickConfirmCreateNewCustomer"
            >
              Add New Customer
            </atom-button>
            <atom-button
              class="ml-1"
              buttonId="cancel-add-customer-button"
              buttonStyle="border"
              buttonColor="secondary"
              @click="onClickCancelCreateNewCustomer"
            >
              Cancel
            </atom-button>
          </div>
        </div>
      </div>
    </section>
    <!-- start modal section -->
    <!-- start confirm create new customer modal -->
    <molecule-modal-dialog
      modalId="modal-confirm-create-new-customer"
      :isTextCenter="true"
      :textTitle="`Do you want to create new customer ?`"
      :textSubtitle="`“${newCustomerData.name}” will add to Customer list.`"
      buttonTextLeft="Confirm"
      @clickButtonLeft="onClickConfirmCreateFromModal"
    ></molecule-modal-dialog>
    <!-- end confirm create new customer modal -->

    <!-- start confirm discard change modal -->
    <molecule-modal-dialog
      modalId="modal-confirm-discard-change"
      :isTextCenter="true"
      textTitle="Do you want to leave without finishing?"
      buttonTextLeft="Confirm"
      @clickButtonLeft="onClickConfirmDiscardChangeFromModal"
    >
    </molecule-modal-dialog>
    <!-- end confirm discard change modal -->
  </div>
</template>

<script>
import { BCollapse } from "bootstrap-vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomCheckbox from "@/components/atoms/AtomCheckbox.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import MoleculeAddressCard from "./MoleculeAddressCard.vue"
import MoleculeCreateEditAddressCard from "./MoleculeCreateEditAddressCard.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import mixinsValidators from "@/mixins/mixinsValidator"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import mixins from "@/mixins"

export default {
  name: "MoleculeSelectCustomer",
  components: {
    BCollapse,
    AtomButton,
    AtomCheckbox,
    AtomInput,
    AtomSelect,
    MoleculeCreateEditAddressCard,
    MoleculeAddressCard,
    MoleculeModalDialog,
  },
  props: {
    customerOption: {
      type: Array,
      required: true,
    },
    businessTypeOption: {
      type: Array,
      required: true,
    },
    selectedCustomerDetail: {
      type: Object,
      required: false,
      default: null,
    },
    selectedCustomerId: {
      type: String,
      required: false,
      default: null,
    },
    forceSelectedCustomer: {
      type: Object,
      required: false,
      default: null,
    },
    isValidateTrigger: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasCreateCustomerPermission: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["onSelectCustomer", "onAddNewCustomer"],
  mixins: [mixins, mixinsValidators, mixinsObjectHelper],
  data() {
    return {
      isAddingNewCustomer: false,
      isValidateNewAddressTrigger: false,
      isErrorOccurInMainAddress: false,
      isErrorOccurInBillingAddress: false,
      isErrorOccurInMailingAddress: false,
      isUseOptionalContractId: false,
      optionalContractId: null,
      isDataChange: false,
      modalId: null,
      errorMessageAtInput: "Please enter valid data",
      errorMessageAtSelector: "Please select data",
      selectedCustomer: null,
      selectedCustomerMainAddress: {},
      selectedCustomerBillingAddress: {},
      selectedCustomerMailingAddress: {},
      newCustomerData: {},
      defaultCustomerData: {
        name: "",
        mainAddress: {
          organizationName: "",
          businessType: null,
          phone: "",
          fax: "",
          email: "",
          country: "Thailand",
          province: null,
          district: null,
          subDistrict: null,
          address: "",
          postalCode: null,
        },
        billingAddress: {
          organizationName: "",
          businessType: null,
          phone: "",
          fax: "",
          email: "",
          country: "Thailand",
          province: null,
          district: null,
          subDistrict: null,
          address: "",
          postalCode: null,
          vat: 7,
        },
        mailingAddress: {
          organizationName: "",
          businessType: null,
          phone: "",
          fax: "",
          email: "",
          country: "Thailand",
          province: null,
          district: null,
          subDistrict: null,
          address: "",
          postalCode: null,
        },
      },
      isShowAllAddress: false,
    }
  },
  computed: {
    isShowCustomerAddresses() {
      return this.hasObjectValue(this.selectedCustomerDetail)
    },
    isErrorAtOptionalContractId() {
      let optionalContractId = this.isEmptyString(this.optionalContractId)
        ? 0
        : Number(this.optionalContractId.replace(/\D/g, ""))
      if (this.isValidateTrigger && this.isUseOptionalContractId) {
        return (
          this.isEmptyString(this.optionalContractId) ||
          optionalContractId < 1 ||
          optionalContractId > 9999
        )
      } else {
        return false
      }
    },
    isErrorAtSelectCustomer() {
      return this.isValidateTrigger
        ? this.isEmptyObject(this.selectedCustomer)
        : false
    },
    isErrorAtNewCustomerName() {
      return this.isValidateNewAddressTrigger
        ? this.isEmptyString(this.newCustomerData.name)
        : false
    },
    isErrorOccur() {
      return (
        this.isErrorAtNewCustomerName ||
        this.isErrorOccurInMainAddress ||
        this.isErrorOccurInBillingAddress ||
        this.isErrorOccurInMailingAddress
      )
    },
    isDisableAddNewCustomerButton() {
      return this.isValidateNewAddressTrigger ? this.isErrorOccur : false
    },
    isNewCustomerDataChangeFromDefault() {
      return this.compareIsChangeObject(
        this.defaultCustomerData,
        this.newCustomerData
      )
    },
  },
  watch: {
    selectedCustomerId: {
      deep: true,
      handler() {
        this.selectedCustomer = this.customerOption.find(
          (customer) => customer.customerId === this.selectedCustomerId
        )
      },
    },
    selectedCustomer: {
      deep: true,
      handler() {
        if (this.selectedCustomer) {
          this.selectCustomer(this.selectedCustomer.customerId)
        } else {
          this.selectCustomer(null)
        }
      },
    },
    selectedCustomerDetail: {
      deep: true,
      handler() {
        if (this.hasObjectValue(this.selectedCustomerDetail)) {
          this.addressDetailDecorator()
        }
      },
    },
    isUseOptionalContractId: {
      deep: true,
      handler() {
        this.$emit(
          "onUpdateOptionalContractId",
          this.isUseOptionalContractId,
          this.optionalContractId
        )
      },
    },
    optionalContractId: {
      deep: true,
      handler() {
        this.$emit(
          "onUpdateOptionalContractId",
          this.isUseOptionalContractId,
          this.optionalContractId
        )
      },
    },
    isErrorAtOptionalContractId: {
      deep: true,
      immediate: true,
      handler() {
        this.$emit(
          "onUpdateErrorAtOptionalIdcId",
          this.isErrorAtOptionalContractId
        )
      },
    },
  },
  mounted() {
    this.newCustomerData = JSON.parse(JSON.stringify(this.defaultCustomerData))
  },
  methods: {
    addressDetailDecorator() {
      const mainAddress = this.selectedCustomerDetail.mainAddress
      this.selectedCustomerMainAddress = {
        organizationName: mainAddress.organizationName,
        businessType: mainAddress.businessType,
        fullAddress: this.getFullAddressDetail(mainAddress),
        email: mainAddress.email,
        phone: mainAddress.phone,
        fax: mainAddress.fax,
      }

      const billingAddress = this.selectedCustomerDetail.billingAddress
      this.selectedCustomerBillingAddress = {
        organizationName: billingAddress.organizationName,
        businessType: billingAddress.businessType,
        fullAddress: this.getFullAddressDetail(billingAddress),
        email: billingAddress.email,
        phone: billingAddress.phone,
        fax: billingAddress.fax,
        vatType: billingAddress.vatType,
      }

      const mailingAddress = this.selectedCustomerDetail.billingAddress
      this.selectedCustomerMailingAddress = {
        organizationName: mailingAddress.organizationName,
        businessType: mailingAddress.businessType,
        fullAddress: this.getFullAddressDetail(mailingAddress),
        email: mailingAddress.email,
        phone: mailingAddress.phone,
        fax: mailingAddress.fax,
      }
    },

    getFullAddressDetail(addressData) {
      return [
        addressData.address,
        addressData.subDistrict,
        addressData.district,
        addressData.province,
        addressData.country === "Thailand" ? "ประเทศไทย" : addressData.country,
        addressData.postalCode,
      ].join(" ")
    },

    setDefaultNewCustomerData() {
      this.newCustomerData = JSON.parse(
        JSON.stringify(this.defaultCustomerData)
      )
    },

    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = ""
    },

    selectCustomer(customerId) {
      this.$emit("onSelectCustomer", customerId)
    },

    onClickAddNewContract() {
      this.selectedCustomer = null
      this.selectedCustomerMainAddress = {}
      this.selectedCustomerBillingAddress = {}
      this.selectedCustomerMailingAddress = {}
      this.isAddingNewCustomer = true
    },

    onShowConfirmCreateModal() {
      this.modalId = "modal-confirm-create-new-customer"
      this.$bvModal.show(this.modalId)
    },

    onClickCloseAddingNewContract() {
      this.$nextTick(() => {
        if (this.isNewCustomerDataChangeFromDefault) {
          this.modalId = "modal-confirm-discard-change"
          this.$bvModal.show(this.modalId)
        } else {
          this.closeModal()
          this.isAddingNewCustomer = false
        }
      })
    },

    onClickConfirmCreateNewCustomer() {
      this.isValidateNewAddressTrigger = true
      this.$nextTick(() => {
        if (!this.isErrorOccur && !this.isErrorAtNewCustomerName) {
          this.onShowConfirmCreateModal()
        }
      })
    },

    onClickCancelCreateNewCustomer() {
      this.$nextTick(() => {
        if (this.isNewCustomerDataChangeFromDefault) {
          this.modalId = "modal-confirm-discard-change"
          this.$bvModal.show(this.modalId)
        } else {
          this.closeModal()
          this.isAddingNewCustomer = false
        }
        this.isValidateNewAddressTrigger = false
      })
    },

    onClickConfirmCreateFromModal() {
      this.closeModal()
      this.isAddingNewCustomer = false
      const newCustomerData = JSON.parse(JSON.stringify(this.newCustomerData))
      this.$emit("onAddNewCustomer", newCustomerData)
    },

    onClickConfirmDiscardChangeFromModal() {
      this.closeModal()
      this.setDefaultNewCustomerData()
      this.isAddingNewCustomer = false
    },

    onVatValueChange(vatValue) {
      this.selectedCustomerBillingAddress.vat = vatValue
    },

    onUpdateErrorInMainAddress(isErrorOccur) {
      this.isErrorOccurInMainAddress = isErrorOccur
    },

    onUpdateErrorInBillingAddress(isErrorOccur) {
      this.isErrorOccurInBillingAddress = isErrorOccur
    },

    onUpdateErrorInMailingAddress(isErrorOccur) {
      this.isErrorOccurInMailingAddress = isErrorOccur
    },

    toggleShowAddress() {
      this.rotateChevron()
      this.isShowAllAddress = !this.isShowAllAddress
    },

    rotateChevron() {
      let rotateUp = this.$refs.chevron.classList.contains("chevron-up")
      let rotateDown = this.$refs.chevron.classList.contains("chevron-down")
      if (!rotateUp && !rotateDown) {
        this.$refs.chevron.classList.add("chevron-up")
      } else if (rotateUp) {
        this.$refs.chevron.classList.remove("chevron-up")
        this.$refs.chevron.classList.add("chevron-down")
      } else {
        this.$refs.chevron.classList.remove("chevron-down")
        this.$refs.chevron.classList.add("chevron-up")
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.select-customer-card ::v-deep input.vs__search::placeholder {
  color: #b9b9c3;
}

.card__header {
  margin-bottom: 14px;
  padding: 21px 21px 0 21px;
}

.card__header--topic {
  font-weight: 500;
  font-size: 18px;
}

.button-text {
  white-space: nowrap;
}

section[name="select-customer"] {
  .select-customer-header {
    display: flex;
    flex-direction: column-reverse;
    padding: 0 21px 21px 21px;
  }

  .selector-container {
    width: 100%;
  }

  .add-new-customer-button-container {
    display: flex;
    justify-content: flex-end;
  }

  .address-card-container {
    padding: 0 21px 21px 21px;
  }

  .optional-contract-id {
    padding-left: 21px;
    padding-right: 21px;
  }

  .optional-contract-id-input__container {
    width: 100%;
  }
}

section[name="add-new-customer"] {
  hr {
    margin-top: 1rem;
    margin-bottom: 1rem;
    border: 0;
    border-top: 1px solid #ebe9f1;
  }

  .add-new-customer-container {
    padding: 0 21px 21px 21px;
  }

  .add-new-customer-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 14px;
  }

  .add-new-customer-topic--text {
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
    color: #b9b9c3;
  }

  .close-icon-container:hover {
    cursor: pointer;
  }

  .input-container {
    width: 100%;
  }
}

.chevron-down {
  transition: transform 0.2s ease-in-out;
  transform: rotate(0deg);
}

.chevron-up {
  transition: transform 0.2s ease-in-out;
  transform: rotate(180deg);
}

.custom-hover-effect {
  color: #b9b9c3;
  &:hover {
    cursor: pointer;
    text-decoration: underline;
    color: #7367f0;
  }
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
  section[name="select-customer"] {
    .select-customer-header {
      flex-direction: initial;
    }

    .selector-container {
      width: 500px;
    }

    .optional-contract-id-input__container {
      width: 325px;
    }

    .add-new-customer-button-container {
      height: 100%;
      margin-top: 24px;
    }
  }

  section[name="add-new-customer"] {
    .input-container {
      width: 325px;
    }
  }
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
}
</style>
